import { useEffect, useRef, useState } from 'react';

// Keyboard navigation for a search-result dropdown. Arrow Up/Down and Tab/Shift+Tab
// move the active row (and re-open the list via onOpen so the keyboard never drives a
// closed/invisible list), Enter chooses it, Escape closes via onClose. Returns the active
// index (for highlighting), a setter (so hover can sync it), the input keydown handler,
// and a ref to put on the <ul> so the active row is scrolled into view.
export function useListKeyboardNav<T>(
  items: T[],
  onChoose: (item: T) => void,
  // Close the dropdown (keeping the input focused) — invoked on Escape.
  onClose: () => void,
  // Ensure the dropdown is open — invoked on the navigation keys so ArrowUp/Down/Tab
  // re-open a list that was force-closed after a pick.
  onOpen?: () => void
) {
  const [activeIndex, setActiveIndex] = useState(-1);
  const listRef = useRef<HTMLUListElement>(null);

  // Reset when the result set changes (e.g. the user types) so we never point at a
  // stale row. Callers should pass a memoized array so this only fires on real changes.
  useEffect(() => {
    setActiveIndex(-1);
  }, [items]);

  // Keep the active row visible as it moves.
  useEffect(() => {
    if (activeIndex < 0) return;
    const el = listRef.current?.children[activeIndex] as HTMLElement | undefined;
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const move = (delta: number) => {
    setActiveIndex((prev) => {
      if (items.length === 0) return -1;
      const next = prev + delta;
      if (next < 0) return 0;
      if (next > items.length - 1) return items.length - 1;
      return next;
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (items.length === 0) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        onOpen?.();
        move(1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        onOpen?.();
        move(-1);
        break;
      case 'Tab':
        // Traverse the list instead of leaving the field; Shift+Tab goes back.
        e.preventDefault();
        onOpen?.();
        move(e.shiftKey ? -1 : 1);
        break;
      case 'Enter':
        if (activeIndex >= 0 && activeIndex < items.length) {
          e.preventDefault();
          onChoose(items[activeIndex]);
        }
        break;
      case 'Escape':
        setActiveIndex(-1);
        onClose();
        break;
      default:
        break;
    }
  };

  return { activeIndex, handleKeyDown, listRef, setActiveIndex };
}
